import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { getMergedCatalogFn } from '#bv/lib/productCatalog'
import { ProductCheckoutPage } from '#bv/components/ProductCheckoutPage'
import { isPartnerOnlyCategory } from '#bv/data/products'
import {
  getCanonicalProductId,
  isProductFamilyTierPage,
} from '#bv/data/productFamilies'
import { productJsonLdScript } from '#bv/lib/productJsonLd'

const SITE_ORIGIN = 'https://blkvault.cc'

const productSearchSchema = z.object({
  tier: z.coerce.number().int().positive().optional(),
})

export const Route = createFileRoute('/products/$productId')({
  validateSearch: productSearchSchema,
  staleTime: 0,
  loader: async ({ params }) => {
    const id = +params.productId
    const catalog = await getMergedCatalogFn()
    const product = catalog.find((x) => x.id === id)
    if (!product || /\bdazn\b/i.test(product.name) || isPartnerOnlyCategory(product.category)) {
      throw new Error('Product not found')
    }
    return { product, catalog }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const { product } = loaderData
    const canonicalId = getCanonicalProductId(product.id)
    const tierPage = isProductFamilyTierPage(product.id)
    const canonicalUrl = `${SITE_ORIGIN}/products/${canonicalId}`
    const title = `${product.name} | PulseDigital`
    const description = product.shortDescription || product.description.slice(0, 160)
    const jsonLd = productJsonLdScript(product, product.stock > 0)
    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: tierPage ? canonicalUrl : `${SITE_ORIGIN}/products/${product.id}` },
        ...(tierPage ? [{ name: 'robots', content: 'noindex,follow' }] : []),
      ],
      links: [{ rel: 'canonical', href: canonicalUrl }],
      scripts: [{ type: 'application/ld+json', children: JSON.stringify(jsonLd) }],
    }
  },
  component: ProductDetailPage,
})

function ProductDetailPage() {
  const { product, catalog } = Route.useLoaderData()
  const { tier: highlightTierId } = Route.useSearch()
  return <ProductCheckoutPage product={product} catalog={catalog} highlightTierId={highlightTierId} />
}
